/**
 * Colour cover helpers for the catalog product studio.
 *
 * A colour cover is the representative image of one product colour. It is
 * independent of the product cover (`isCover` / `isPrimary`) and of gallery
 * order — selecting one only rewrites `colorCoverOptionValueIds` and never
 * touches `isCover` or the position of any item in the list.
 */

const COLOR_DIMENSION_KEYS = [
  "color",
  "colors",
  "colour",
  "colours",
  // Albanian — the live catalog names this dimension "Ngjyra".
  "ngjyra",
  "ngjyre",
  "ngjyrat",
  "ngjyres",
];

/** Lowercases, strips diacritics and drops separators: "Ngjyrë" → "ngjyre". */
export function normalizeDimensionKey(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

export function isColorDimensionName(...names) {
  return names.some((name) =>
    COLOR_DIMENSION_KEYS.includes(normalizeDimensionKey(name))
  );
}

/**
 * The product's colours, each with the ids of the variants wearing it.
 *
 * Built from the structured option definitions and variant option values, so a
 * colour is never inferred from a title or SKU.
 */
export function buildProductColors(options = [], variants = []) {
  const colorOption = (options || []).find((option) =>
    isColorDimensionName(option?.key, option?.name)
  );

  if (!colorOption) return [];

  return (colorOption.values || []).map((value) => ({
    optionValueId: value.id,
    value: value.value,
    variantIds: (variants || [])
      .filter((variant) =>
        (variant.optionValues || []).some(
          (optionValue) => optionValue.optionValueId === value.id
        )
      )
      .map((variant) => variant.id),
  }));
}

/**
 * Whether a media item may represent a colour.
 *
 * An item scoped to specific variants must include at least one variant of that
 * colour, so a Krem-only image can never be picked as the Zi colour cover. An
 * unscoped item is a product-wide image and is eligible for every colour —
 * which is what lets a merchant give a colour an explicit cover even when its
 * size variants have incomplete media assignments.
 *
 * Mirrors the rule the API enforces; the API remains the authority.
 */
export function mediaCanCoverColor(item, color) {
  if (!color) return false;

  const variantIds = (item?.variantIds || []).map(Number);
  if (variantIds.length === 0) return true;

  const colorVariantIds = new Set((color.variantIds || []).map(Number));
  return variantIds.some((variantId) => colorVariantIds.has(variantId));
}

/**
 * Assigns `colorOptionValueId`'s cover to the item at `index`, clearing that
 * colour from every other item. Gallery order, `isCover` and every other field
 * are returned untouched — one colour has at most one cover, and choosing it
 * changes nothing else.
 */
export function markCatalogColorCover(items, index, colorOptionValueId) {
  return items.map((item, itemIndex) => {
    const current = (item.colorCoverOptionValueIds || []).filter(
      (valueId) => valueId !== colorOptionValueId
    );

    const next =
      itemIndex === index ? [...current, colorOptionValueId] : current;

    return {
      ...item,
      colorCoverOptionValueIds: next,
    };
  });
}

/** Clears a colour's cover entirely, leaving the product without one. */
export function clearCatalogColorCover(items, colorOptionValueId) {
  return items.map((item) => ({
    ...item,
    colorCoverOptionValueIds: (item.colorCoverOptionValueIds || []).filter(
      (valueId) => valueId !== colorOptionValueId
    ),
  }));
}

/** Colours this media item currently covers, in product colour order. */
export function getItemColorCovers(item, colors = []) {
  const assigned = new Set(item?.colorCoverOptionValueIds || []);
  return colors.filter((color) => assigned.has(color.optionValueId));
}

/**
 * Drops colour covers that are no longer valid before saving — a colour the
 * merchant removed, or an image whose variant assignments no longer include
 * that colour. Keeps the CMS from sending a payload the API would reject.
 */
export function pruneInvalidColorCovers(items, colors = []) {
  const colorById = new Map(
    colors.map((color) => [color.optionValueId, color])
  );

  return items.map((item) => ({
    ...item,
    colorCoverOptionValueIds: (item.colorCoverOptionValueIds || []).filter(
      (valueId) => {
        const color = colorById.get(valueId);
        return Boolean(color) && mediaCanCoverColor(item, color);
      }
    ),
  }));
}
